import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { Bloom } from '../components/Bloom';
import { CTAButton } from '../components/CTAButton';
import { FeatureSection } from '../components/FeatureSection';
import { YelpReportExample } from '../components/YelpReportExample';
import { SEOHead } from '../components/SEOHead';
import type { Feature } from '../data/howItWorks';

/* 2026-09-15 (Kevin, three-services restructure): full rewrite of this
   page per the follow-up handoff. Billing corrected to what Viridia
   actually runs (Yelp's centralized partner billing -- Yelp invoices
   Viridia, Viridia invoices the client at month end; the old "paid by you
   directly to Yelp, we never hold your ad budget" copy described the
   wrong model and is gone). Fee structure replaced with the locked
   2026-09-14 numbers (15% / $200 min per page / 10% above $5,000). The
   Terms section is dropped from this page per Kevin (2026-09-15: "it adds
   uncertainty") -- the long version now lives on /terms under "Yelp Ads
   Management". "What you get" rewritten to name the six services instead
   of describing the strategy behind them. Badge label corrected to "Yelp
   Advertising Premier Partner" throughout. Binding rules unchanged from
   the 2026-09-14 build: no review language of any kind, nothing readable
   as review gating, no "audit", house style (no em dashes, no AI tells).
   Fixed one leftover from the 2026-09-14 build during this pass: "your
   first quarterly review after the third" used "review" in the generic
   business-meeting sense, not a customer review, but this page's own rule
   is no review language of any kind, so it is now "check-in". Staged
   only; not published to the domain. */

const whatYouGet: Feature[] = [{
  title: 'Ad targeting.',
  body: 'Service categories, keywords, service area, and pacing tuned to the jobs you actually want, checked every week.'
}, {
  title: 'Page upgrade optimization.',
  body: 'Photos, categories, specialties, hours, and quote-request settings, the details that turn a click into a call. Done before a dollar of ad spend moves.'
}, {
  title: 'On-page SEO.',
  body: "Your Yelp page written so Yelp's search and Google both understand what you do and where you do it."
}, {
  title: 'Content.',
  body: 'Service descriptions, photo captions, and page updates that keep the page working between ad clicks.'
}, {
  title: 'Enterprise-level reporting.',
  body: "A monthly report pulled straight from Yelp's advertising API, joined to what happened after the click: calls answered, quote requests answered and how fast, estimates booked. The number Yelp's dashboard never shows you is cost per booked job. We show it. Every quarter we sit down with you and decide what changes."
}, {
  title: 'A straight answer every month.',
  body: 'If the account is not working, you hear it from us first, with what we would change. If the answer is "stop spending," we say that too.'
}];
const whyViridia: Feature[] = [{
  title: 'Two former Yelp reps',
  body: "Kevin spent five years inside Yelp's ad business as a Senior Elite Account Executive. Nate was a Senior Mid-Market Client Partner there, managing $2.5 million in client ad spend."
}, {
  title: 'High-spend experience',
  body: "Campaigns at the budgets where Yelp's own reps pay attention, and the promotions and page settings that come with them."
}, {
  title: 'Home services only',
  body: 'HVAC, plumbing, electrical, roofing, painting, landscaping, cleaning, pest, garage doors. We know what a good month looks like in your trade and your metro.'
}];
const howItStarts = [{
  title: 'A 15-minute call',
  body: 'You tell us what you spend and what you expect. We tell you honestly whether we can beat it.'
}, {
  title: "Your campaign moves under Viridia's billing",
  body: "Your Yelp campaign moves under Viridia's partner billing. Same budget, same ads; the invoice now comes from us instead of Yelp."
}, {
  title: 'Week one, week two, first report',
  body: 'Week one: the listing gets fixed. Week two: the budget gets managed. Your first monthly report lands 30 days later, your first quarterly check-in after the third.'
}];
const feeTable = [{
  spend: '$1,000',
  fee: '$200'
}, {
  spend: '$2,000',
  fee: '$300'
}, {
  spend: '$3,000',
  fee: '$450'
}, {
  spend: '$5,000',
  fee: '$750'
}];
export function YelpAds() {
  return <>
      <SEOHead title="Yelp Ads Management | Run by a Former Yelp Rep | Viridia Analytics" description="A former Yelp ad rep manages your Yelp advertising: listing fixes, weekly budget management, and a monthly report tied to booked jobs. 15% of ad spend, $200/month minimum per Yelp page, month to month." canonical="/yelp-ads" data-id="element-3656" data-mp="16uvojqhzaold" />

      <PageHeader label="Yelp ads management" title="Yelp ads, run by someone who worked there." intro="Five years inside Yelp's ad business. Now on your side of the table. We manage your Yelp advertising so the budget turns into booked jobs, and we show you exactly where every dollar went." data-id="element-3657" data-mp="33tb5eea04des">
        <div className="flex flex-wrap gap-4" data-id="element-3658" data-mp="3ige2ijuewk1l">
          <CTAButton to="/contact" data-id="element-3659" data-mp="2ktx6rj1buehy">See if we're a fit</CTAButton>
          <CTAButton href="#pricing" variant="ghost" data-id="element-3660" data-mp="2kvi41gf10bji">
            Pricing
          </CTAButton>
        </div>
      </PageHeader>

      <FeatureSection id="what-you-get" label="What you get" title="Six things, working together." body="Named, not vague. Here is exactly what we do to your account." features={whatYouGet} columns={3} data-id="element-3661" data-mp="2puux4iwhqeuj" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-3662" data-mp="a143xlpqdwyk">
        <div className="mx-auto w-full max-w-4xl px-6 py-20 lg:py-24" data-id="element-3663" data-mp="e268mi2f317x">
          <SectionLabel data-id="element-3664" data-mp="2b3c16o0nanhm">Example report</SectionLabel>
          <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3665" data-mp="2b67xy90rb64h">
            What you see every month.
          </h2>
          <div className="mt-10" data-id="element-3666" data-mp="2k3ib5zx4x5c1">
            <YelpReportExample data-id="element-3667" data-mp="2k2p8ba1aqzz6" />
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3668" data-mp="2f68blrg17dah">
        <Bloom size="md" className="right-0 top-[-6rem]" data-id="element-3669" data-mp="1rgxk70kkmv90" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3670" data-mp="1nessw8hliapj">
          <SectionLabel data-id="element-3671" data-mp="1ncg3xbdf702z">Why Viridia</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3672" data-mp="216z89scxcdnx">
            Managed by people who worked the other side of the table.
          </h2>

          <ul className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] sm:grid-cols-3" data-id="element-3673" data-mp="18ol6xisdq9k3">
            {whyViridia.map((w, i) => <motion.li key={w.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="bg-[#0d0d0d] p-8" data-id="element-3674" data-mp="3fddg4kygkvu7" data-mpi={i}>
                <h3 className="text-base font-semibold text-white" data-id="element-3675" data-mp="3bctizhdvxs82">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-3676" data-mp="2xjl6lh13963">{w.body}</p>
              </motion.li>)}
          </ul>

          {/* Premier Partner badge -- real asset supplied by Kevin
              (handoffs/inbox/2026-09-14-yelp-ads-page-and-rename.md, item 3).
              Label corrected to "Yelp Advertising Premier Partner" per the
              2026-09-15 follow-up handoff, item 5a, to match the badge
              itself. Sits on a white disc so the badge's own light interior
              reads correctly against the dark page. */}
          <div className="mt-8 flex flex-col items-start gap-6 rounded-2xl border border-emerald-500/30 bg-[#0d0d0d] p-7 sm:flex-row sm:items-center" data-id="element-3677" data-mp="3aaxt2cg9a2be">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-white p-1.5" data-id="element-3678" data-mp="35ajk8s6tyocb">
              <img src="https://cdn.magicpatterns.com/uploads/cAKrsUQMQwT8woPxxf5mVS/yelp-premiere-partner-1.png" alt="Yelp Advertising Premier Partner" className="h-full w-full object-contain" data-id="element-3679" data-mp="30f44tvt6ckds" />
            </div>
            <div data-id="element-3680" data-mp="3o3x3w153f8cm">
              <h3 className="text-base font-semibold text-white" data-id="element-3681" data-mp="3o3dvpc58vybs">Yelp Advertising Premier Partner</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-3682" data-mp="3j9swxhvtv0bj">
                Partner status means access to Yelp's current promotions for new and increased
                ad spend. Ask us what is running this quarter.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3683" data-mp="j4q2l7z4jyk9">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-3684" data-mp="n6bn9fhwhh50" />
        <div className="mx-auto w-full max-w-3xl px-6 py-20 lg:py-28" data-id="element-3685" data-mp="n8ebbtzht073">
          <SectionLabel data-id="element-3686" data-mp="s4mxvsg0rvoa">Pricing</SectionLabel>

          <article className="mt-10 rounded-3xl border border-emerald-500/40 bg-[#0e1310] p-8 text-center sm:p-12" data-id="element-3687" data-mp="x4hnztrz0h8a">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-500" data-id="element-3688" data-mp="1243znys5hpez">
              Yelp ads management
            </h3>
            <p className="mt-8 text-lg leading-relaxed text-neutral-200 sm:text-xl" data-id="element-3689" data-mp="163mlcwrsq4iv">
              15% of your monthly Yelp ad spend.
              <br data-id="element-3690" data-mp="1fuzc1l1uyonb" />
              $200 a month minimum per Yelp page.
              <br data-id="element-3691" data-mp="1fu7fo80fsmvz" />
              Above $5,000 a month, the extra is billed at 10%.
            </p>

            <div className="mx-auto mt-10 max-w-md overflow-hidden rounded-xl border border-[#1c2420]" data-id="element-3692" data-mp="1kvwrvqzeb6ln">
              <div className="grid grid-cols-2 bg-[#131a16] text-left font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-id="element-3693" data-mp="1pr2deoovyj97">
                <span className="px-4 py-2.5" data-id="element-3694" data-mp="3bbg4bcg7n4b2">You spend</span>
                <span className="px-4 py-2.5" data-id="element-3695" data-mp="3b9vbbr9jntfb">You pay</span>
              </div>
              {feeTable.map(row => <div key={row.spend} className="grid grid-cols-2 border-t border-[#1c2420] text-left text-sm text-neutral-200" data-id="element-3696" data-mp="3l1r9t3fjllk2">
                  <span className="px-4 py-2.5" data-id="element-3697" data-mp="3q2yjx955qwtw">{row.spend}</span>
                  <span className="px-4 py-2.5 font-semibold text-emerald-500" data-id="element-3698" data-mp="3uym4tcclx3tl">{row.fee}</span>
                </div>)}
            </div>

            <p className="mx-auto mt-8 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-3699" data-mp="7v8yp0e8iwzn">
              Billed on the 1st for the month ahead: your budget plus the fee, on the card you
              put on file. Yelp under-delivers? The difference comes off next month.
            </p>

            <p className="mt-8 border-t border-[#1c2420] pt-8 text-sm leading-relaxed text-neutral-300" data-id="element-3700" data-mp="29ofbt96grx5t">
              Month to month. 30 days' notice. One charge on the 1st for the month ahead.
              <br data-id="element-3701" data-mp="1xajqnbnys3w5" />
              <Link to="/terms#yelp-ads-management" className="text-emerald-500 hover:text-emerald-400" data-id="element-3702" data-mp="1sd0cebr9nr47">
                Full terms
              </Link>
            </p>
          </article>

          <div className="mt-8 space-y-4" data-id="element-3703" data-mp="2l1xhteadptfj">
            <div className="rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7 text-center" data-id="element-3704" data-mp="2l0w75ax9z8yc">
              <h3 className="text-base font-semibold text-white" data-id="element-3705" data-mp="2g3w1v2892a8a">Response layer, $297 a month</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-3706" data-mp="2b2q6ewn7g8v2">
                Auto-reply on quote requests, missed-call text-back, after-hours coverage. Because
                when the ads work, more calls come in, and the ones you miss are the expensive
                ones.
              </p>
            </div>
            <div className="rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7 text-center" data-id="element-3707" data-mp="vlpp5mg4u0fq">
              <p className="text-sm leading-relaxed text-neutral-400" data-id="element-3708" data-mp="x4psmpqda778">
                You set the budget; we never spend past it. Minimum ad spend $1,000 a month. Ask
                about current Yelp partner promotions for new and increased spend.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-3709" data-mp="s6wi5ki16r15">
        <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-3710" data-mp="j4ex39e444fq" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3711" data-mp="j5yej3bi8lj3">
          <SectionLabel data-id="element-3712" data-mp="4gacu0zevkuu">How it starts</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3713" data-mp="4deggh37p48i">
            Three steps, thirty days to your first report.
          </h2>

          <ol className="mt-12 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-3" data-id="element-3714" data-mp="3rl096z21jdn2">
            {howItStarts.map((s, i) => <motion.li key={s.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="border-t border-[#1c1c1c] pt-5" data-id="element-3715" data-mp="3cxi3pygnmezl" data-mpi={i}>
                <p className="font-mono text-xs tracking-[0.2em] text-neutral-600" data-id="element-3716" data-mp="3gybt6w9qw1ul">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="mt-3 text-base font-semibold text-white" data-id="element-3717" data-mp="3qxj2n8dczccf">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-3718" data-mp="3qvgc8n8anh76">{s.body}</p>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section className="relative overflow-hidden" data-id="element-3719" data-mp="4ihwar8r853u">
        <div className="mx-auto w-full max-w-3xl px-6 py-12 text-center" data-id="element-3720" data-mp="igy30i6m9vlz">
          <p className="text-xs leading-relaxed text-neutral-600" data-id="element-3721" data-mp="diefidcdkbri">
            Viridia Analytics is a Yelp Advertising Premier Partner and an independent agency; it
            is not owned by or affiliated with Yelp Inc.
          </p>
        </div>
      </section>
    </>;
}